import { usageData, usageErrors, UsageUnavailableError } from '$lib/api-error-handling';
import type { BillingRefusalDetails } from '$lib/api-error-kind';
import {
	getBillingSummary,
	getOrgUsageSummary,
	getProjectUsageSummary,
	getUserBillingSummary,
	getUserUsageSummary,
	listOrgUsageHistory,
	listProjectUsageHistory,
	listUserUsageHistory,
} from '$lib/api/sdk.gen';
import { ResourceType, type BillingSummary, type UsageQuantity } from '$lib/api/types.gen';
import { loadAuthzActions } from '$lib/stores/authz';

import { ANOTHER_USER_NEEDS_TO_SET_UP, CONTACT_SUPPORT_TO_SET_UP } from './billing-messages';

export type UsageScope = { type: 'organization' | 'project' | 'user'; id: string };
export type UsagePeriod = 'month' | 'previous' | '7d' | '30d';
export type UsageGranularity = 'day' | 'hour';
export type BillingBalance =
	| BillingSummary
	| ({ kind: 'not_provisioned' } & BillingRefusalDetails)
	| null;

export async function loadBillingBalance(scope: {
	type: 'organization' | 'user';
	id: string;
}): Promise<BillingBalance> {
	const options = { meta: { errors: usageErrors } };
	try {
		if (scope.type === 'organization') {
			const [canViewBilling] = await loadAuthzActions({
				resource: { type: ResourceType.ORG, id: scope.id },
				check: 'org:view_billing',
			});
			if (!canViewBilling) return null;
			return await usageData(getBillingSummary({ path: { org_id: scope.id }, ...options }));
		}
		return await usageData(getUserBillingSummary({ path: { user_id: scope.id }, ...options }));
	} catch (error) {
		if (error instanceof UsageUnavailableError) {
			return error.kind === 'BILLING_NOT_PROVISIONED'
				? { kind: 'not_provisioned', ...error.details }
				: null;
		}
		throw error;
	}
}

/**
 * What the balance card says. `billingPageLinked`: the card can link to the Billing page, where
 * billing is set up, so a payer that has none is pointed there (`setUp`) when its summary says
 * self-serve setup is on (or the refusal that stands in for it says so). Otherwise it is support.
 */
export function describeBillingBalance(
	balance: Exclude<BillingBalance, null>,
	{
		billingPageLinked = false,
		anotherUserNamed = false,
	}: { billingPageLinked?: boolean; anotherUserNamed?: boolean } = {},
) {
	const setupAvailable = 'kind' in balance ? balance.setupAvailable : balance.setup_available;
	const canBeSetUp = billingPageLinked && setupAvailable === true;
	if ('kind' in balance && anotherUserNamed) {
		// The refusal is for want of another user's billing: no page of the viewer's fixes it.
		return {
			kind: 'message' as const,
			title: 'Billing not set up',
			description: `No Dream Bucks balance is available. ${ANOTHER_USER_NEEDS_TO_SET_UP}`,
			setUp: undefined,
		};
	}
	if ('kind' in balance || balance.account_state === 'not_provisioned') {
		return {
			kind: 'message' as const,
			title: 'Billing not set up',
			description: `No Dream Bucks balance is available until billing is set up.${
				canBeSetUp ? '' : ` ${CONTACT_SUPPORT_TO_SET_UP}`
			}`,
			setUp: canBeSetUp ? { label: 'Set up billing' } : undefined,
		};
	}
	if (balance.account_state === 'provisioning') {
		return {
			kind: 'message' as const,
			title: 'Billing setup in progress',
			description: 'The balance will appear when setup finishes.',
		};
	}
	if (balance.account_state === 'failed') {
		return {
			kind: 'message' as const,
			title: 'Billing setup failed',
			description: 'The balance will appear after billing is set up.',
			// Only with a summary that says self-serve is on: it is the one that can finish the setup.
			setUp: billingPageLinked && setupAvailable ? { label: 'Try setting up again' } : undefined,
		};
	}
	if (balance.balance_state === 'exhausted') {
		return {
			kind: 'amount' as const,
			quantity: balance.balance_dream_bucks ?? '0',
			status: 'Exhausted',
		};
	}
	if (balance.balance_state === 'funded' && balance.balance_dream_bucks !== null) {
		return {
			kind: 'amount' as const,
			quantity: balance.balance_dream_bucks,
			status: null,
		};
	}
	return {
		kind: 'message' as const,
		title: 'Balance temporarily unavailable',
		description: balance.metronome_unavailable
			? 'Couldn’t reach the balance provider.'
			: 'The current balance can’t be confirmed yet.',
	};
}

export function watchBillingBalance(
	initial: Promise<BillingBalance>,
	refresh: () => Promise<BillingBalance>,
	onBalance: (balance: BillingBalance) => void,
) {
	let stopped = false;
	let timer: ReturnType<typeof setTimeout> | undefined;

	async function update(promise: Promise<BillingBalance>) {
		try {
			const balance = await promise;
			if (stopped) return;
			onBalance(balance);
			if (balance && 'balance_refreshing' in balance && balance.balance_refreshing) {
				timer = setTimeout(() => {
					void update(Promise.resolve().then(refresh));
				}, 1_000);
			}
		} catch {
			// The API interceptor reports non-402 errors globally. Keep the last snapshot.
		}
	}

	void update(initial);
	return () => {
		stopped = true;
		clearTimeout(timer);
	};
}

export function usageFilters(url: URL) {
	const period = url.searchParams.get('period');
	const granularity = url.searchParams.get('granularity');
	return {
		period: period === 'previous' || period === '7d' || period === '30d' ? period : 'month',
		granularity: granularity === 'hour' ? 'hour' : 'day',
	} satisfies { period: UsagePeriod; granularity: UsageGranularity };
}

export function usageRange(period: UsagePeriod, now = new Date()) {
	if (period === 'month') return undefined;
	if (period === 'previous') {
		const to = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
		return {
			from_ts: Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1) / 1000,
			to_ts: to / 1000,
		};
	}
	return {
		from_ts: (now.getTime() - (period === '7d' ? 7 : 30) * 86_400_000) / 1000,
		to_ts: now.getTime() / 1000,
	};
}

export function formatUsageQuantity(value: UsageQuantity) {
	const [whole, fraction] = value.quantity.split('.');
	const integer = /^\d+$/.test(whole) ? BigInt(whole).toLocaleString('en-US') : whole;
	return `${integer}${fraction ? `.${fraction}` : ''} ${usageUnit(value.unit)}`;
}

/** Compact display only; exact quantities remain available in the detail table. */
export function formatUsageAmount(value: string) {
	const nanos = usageQuantityNanos(value);
	if (nanos > 0n && nanos < 100_000n) return '<0.0001';
	const rounded = (nanos + 50_000n) / 100_000n;
	const whole = (rounded / 10_000n).toLocaleString('en-US');
	const fraction = (rounded % 10_000n).toString().padStart(4, '0').replace(/0+$/, '');
	return `${whole}${fraction ? `.${fraction}` : ''}`;
}

export function formatUsageTimestamp(seconds: number) {
	return new Intl.DateTimeFormat('en-US', {
		timeZone: 'UTC',
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23',
	}).format(new Date(seconds * 1000));
}

export function usageUnit(unit: string) {
	return unit === 'dream_bucks' ? 'DB' : unit;
}

export function usageQuantityNanos(value: string) {
	const [whole, fraction = ''] = value.split('.');
	return BigInt(whole) * 1_000_000_000n + BigInt(fraction.padEnd(9, '0'));
}

export function usageBarHeight(quantity: string, maximum: string) {
	const max = usageQuantityNanos(maximum);
	const value = usageQuantityNanos(quantity);
	if (max === 0n || value === 0n) return 0;
	return Math.max(2, Number((value * 10_000n) / max) / 100);
}

/**
 * `billingPageLinked`: the page points to the Billing page, where billing is set up (the refusal
 * says self-serve setup is on), so support isn't the thing to suggest. `anotherUserNamed`: the
 * refusal is for want of another user's billing, which only they can set up.
 */
export function usageUnavailableMessage(
	error: UsageUnavailableError,
	{
		billingPageLinked = false,
		anotherUserNamed = false,
	}: { billingPageLinked?: boolean; anotherUserNamed?: boolean } = {},
) {
	return error.kind === 'BILLING_DISABLED'
		? 'Usage reporting is unavailable because billing is disabled on this server.'
		: error.kind === 'BILLING_NOT_PROVISIONED' && anotherUserNamed
			? `Usage reporting is unavailable for this period. ${ANOTHER_USER_NEEDS_TO_SET_UP}`
			: error.kind === 'BILLING_NOT_PROVISIONED'
				? `Usage reporting is unavailable because billing has not been set up for usage in this period.${
						billingPageLinked ? '' : ` ${CONTACT_SUPPORT_TO_SET_UP}`
					}`
				: 'Usage reporting is unavailable for this period.';
}

export function usageCategory(category: string) {
	return category === 'dream_bucks'
		? 'Dream Bucks'
		: category.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatUsageDate(seconds: number, granularity: UsageGranularity = 'day') {
	return new Intl.DateTimeFormat('en-US', {
		timeZone: 'UTC',
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		...(granularity === 'hour' ? { hour: 'numeric' as const, hour12: false } : {}),
	}).format(new Date(seconds * 1000));
}

export function loadUsage(scope: UsageScope, url: URL) {
	const filters = usageFilters(url);
	const range = usageRange(filters.period);
	const summaryQuery = range;
	const historyQuery = { ...range, granularity: filters.granularity, page_size: 100 };
	const options = { meta: { errors: usageErrors } };

	const summary = usageData(
		scope.type === 'organization'
			? getOrgUsageSummary({ path: { org_id: scope.id }, query: summaryQuery, ...options })
			: scope.type === 'project'
				? getProjectUsageSummary({
						path: { proj_id: scope.id },
						query: summaryQuery,
						...options,
					})
				: getUserUsageSummary({
						path: { user_id: scope.id },
						query: summaryQuery,
						...options,
					}),
	);

	function loadHistory(cursor?: string) {
		const query = cursor ? { cursor } : historyQuery;
		return usageData(
			scope.type === 'organization'
				? listOrgUsageHistory({ path: { org_id: scope.id }, query, ...options })
				: scope.type === 'project'
					? listProjectUsageHistory({ path: { proj_id: scope.id }, query, ...options })
					: listUserUsageHistory({ path: { user_id: scope.id }, query, ...options }),
		);
	}

	return {
		filters,
		promise: Promise.all([summary, loadHistory()]).then(([summary, history]) => ({
			summary,
			history,
		})),
		loadHistory,
	};
}
